// The climate a person is shown of their own material (ADR 0019; design §2.5 and §2.14 items 4 and 7; the owner's pick
// E3): the closed word the summary or the daily Slack look answered while executive intelligence is on, and the sentence
// that it is counted, never named, into the organisation pulse. Shown only where the server sent a climate (it sends one
// only under the executive floor). A Slack check's word is not shown: the meeting's Slack tab learns only that one was
// counted (`climateCounted`, a yes or no), so the person cannot see that word — the owner kept it so, as built (ADR 0019,
// Open 6). A climate is a model's reading of a conversation, never a state: it is worded with its qualifier
// ("Climate:"), never as a pill or beside a state word (design system 05-content §6.1, A25), and its signals are joined by
// the locale's list (05-content §8). Pure; unit-tested under node.

import type { ClimateDto } from '../../../shared/api-types.js';
import { CLIMATE_SIGNAL_LABELS } from '../../../shared/enums.js';
import { formatList } from '../../ui/format.js';

/** "strained — deadline pressure and unclear ownership", or the word alone when no signal was answered. */
export function climateReading(climate: ClimateDto): string {
  const signals = climate.signals.map((signal) => CLIMATE_SIGNAL_LABELS[signal]);
  return signals.length > 0 ? `${climate.climate} — ${formatList(signals)}` : climate.climate;
}

/**
 * The meeting page's Climate row (design §2.5: "Climate: strained — deadline pressure, unclear ownership · counted, never
 * named, into the organisation pulse"), the signals joined as the locale joins a list.
 */
export function meetingClimateText(climate: ClimateDto): string {
  return `Climate: ${climateReading(climate)} · counted, never named, into the organisation pulse`;
}

/** The Slack today tab's line (design §2.5: "Today’s climate: mixed · counted into the organisation pulse"). */
export function slackTodayClimateText(climate: ClimateDto): string {
  return `Today’s climate: ${climateReading(climate)} · counted into the organisation pulse`;
}

/**
 * The Slack today tab's author line (design §2.14 item 7): "Only you can see this." gains the day's climate — said exactly
 * when the look shown answered one, which the server sends only while executive intelligence is on.
 */
export function slackTodayCaveatText(climateShown: boolean): string {
  return climateShown
    ? 'Written by Calis from your Slack. Open the messages before acting. Only you can see this; the day’s climate is counted, never named, into the organisation pulse.'
    : 'Written by Calis from your Slack. Open the messages before acting. Only you can see this.';
}

/**
 * The meeting's Slack tab's caution line (design §2.14 item 7, "likewise"): said of the check shown exactly when the
 * server's `climateCounted` is yes — the check holds a climate mark a report may count (not one it will: ADR 0019, stage 5
 * adaptation 58) — never of a check made without one.
 */
export function slackCheckCaveatText(climateCounted: boolean): string {
  return climateCounted
    ? 'Check the linked messages before acting. Only you can see this; the climate of what it read is counted, never named, into the organisation pulse.'
    : 'Check the linked messages before acting. Only you can see this.';
}

/** What "only you can see them" becomes while executive intelligence is on (design §2.14 item 7: Settings and the check's dialog). */
export const ONLY_YOU_BUT_CLIMATE = 'only you can see them, apart from one closed climate word counted into the organisation pulse';
